import { Link, useParams } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { InvoicePreview, useInvoicePdf } from '../../components/admin/orders/InvoicePreview'
import { InvoiceSendPanel } from '../../components/admin/orders/InvoiceSendPanel'
import { ApiRequestError } from '../../lib/api'
import { useOrder } from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'

/**
 * Email invoice, as a page (`/admin/orders/:id/invoice`, `docs/22` §
 * "Invoice"): the invoice the customer will get, whole, on the left, and on
 * the right who it goes to and the Send that sends it — held in view while
 * the invoice scrolls. On anything narrower than a laptop the panel comes
 * first and the invoice under it: the addresses are what the stylist came
 * to type.
 *
 * Under Orders, which stays lit: its address is under `/admin/orders`.
 */
export function InvoicePage() {
  const { orderId = '' } = useParams<{ orderId: string }>()
  const { token } = useStaffToken()
  const query = useOrder(token, orderId)
  const detail = query.data

  return (
    <div>
      <Link to={`/admin/orders/${encodeURIComponent(orderId)}`} className="st-btn-quiet -ml-2 mb-3 inline-flex">
        <ChevronLeft size={14} aria-hidden />
        {detail ? detail.order.orderNo : 'Order'}
      </Link>
      {query.isPending ? <p className="st-meta">Loading…</p> : null}
      {query.isError ? (
        <p className="st-alert" role="alert">
          {query.error instanceof ApiRequestError && query.error.status === 404
            ? "There's no order here. It may be another boutique's, or the link is wrong."
            : 'Couldn’t load this order. Try again in a moment.'}
        </p>
      ) : null}
      {detail ? <InvoiceView detail={detail} /> : null}
    </div>
  )
}

/** The PDF is fetched here, once, for the preview and for Download PDF in the panel. */
function InvoiceView({ detail }: { readonly detail: OrderDetailResponse }) {
  const { pdf, retry } = useInvoicePdf(detail)
  return (
    <>
      <div className="mb-5">
        <div className="st-eyebrow mb-1">Email invoice</div>
        <h1 className="st-display text-3xl tabular-nums">{detail.order.orderNo}</h1>
        <p className="st-meta mt-1 text-sm">
          {detail.order.customerName ? `${detail.order.customerName}. ` : ''}What they&rsquo;ll get: this invoice in the email, with
          the PDF attached.
        </p>
      </div>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 lg:sticky lg:top-5 lg:col-start-2 lg:row-start-1">
          <InvoiceSendPanel detail={detail} pdf={pdf.kind === 'ready' ? pdf.blob : null} />
        </div>
        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          <InvoicePreview orderNo={detail.order.orderNo} pdf={pdf} onRetry={retry} />
        </div>
      </div>
    </>
  )
}
